import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Phone, Mail, Calendar, Clock, Users, Wrench,
  Play, CheckCircle, Camera, Image as ImageIcon, Loader2, Send,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_LABELS = {
  planifie: { text: "Planifié", color: "bg-blue-100 text-blue-800" },
  en_cours: { text: "En cours", color: "bg-amber-100 text-amber-800" },
  termine:  { text: "Terminé", color: "bg-green-100 text-green-800" },
  annule:   { text: "Annulé", color: "bg-red-100 text-red-800" },
};

/**
 * Vue mobile simplifiée pour ouvriers sur le terrain.
 * Accessible via QR code (/sav/intervention/:savId).
 * Permet : voir coordonnées, démarrer/terminer, ajouter note, photos avant/après.
 */
export default function SAVMobileWorkerPage() {
  const { savId } = useParams();
  const [sav, setSav] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [note, setNote] = useState("");
  const [sendingNote, setSendingNote] = useState(false);
  const [uploadingLabel, setUploadingLabel] = useState(null); // "before" | "after"
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);

  const fetchSav = async () => {
    try {
      const res = await axios.get(`${API_URL}/sav/${savId}/worker-view`, { headers: getHeaders() });
      setSav(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur de chargement";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSav(); }, [savId]);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const res = await axios.patch(`${API_URL}/sav/${savId}/worker-update`,
        { status: newStatus },
        { headers: getHeaders() });
      setSav((prev) => ({ ...prev, ...res.data }));
      toast.success(newStatus === "en_cours" ? "Intervention démarrée" : "Intervention terminée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setUpdating(false);
    }
  };

  const handleSendNote = async () => {
    if (!note.trim()) return;
    setSendingNote(true);
    try {
      const res = await axios.patch(`${API_URL}/sav/${savId}/worker-update`,
        { note: note.trim(), note_kind: "comment" },
        { headers: getHeaders() });
      setSav((prev) => ({ ...prev, ...res.data }));
      setNote("");
      toast.success("Note ajoutée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSendingNote(false);
    }
  };

  const handlePhotoUpload = async (file, label) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez choisir une image");
      return;
    }
    setUploadingLabel(label);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API_URL}/sav/${savId}/worker-photo?label=${label}`,
        form,
        { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      toast.success(`Photo ${label === "before" ? "« avant »" : "« après »"} ajoutée`);
      await fetchSav();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploadingLabel(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!sav) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
        <Wrench className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground mb-4">Intervention introuvable ou accès refusé</p>
        <Link to="/sav"><Button variant="outline">Retour à la liste</Button></Link>
      </div>
    );
  }

  const statusInfo = STATUS_LABELS[sav.status] || { text: sav.status, color: "bg-gray-100 text-gray-800" };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="sav-mobile-worker-page">
      {/* Sticky header */}
      <div className="bg-primary text-white sticky top-0 z-10 shadow">
        <div className="flex items-center justify-between p-3">
          <Link to="/sav" className="flex items-center gap-1 text-white/90 hover:text-white" data-testid="back-to-sav-list">
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm">Retour</span>
          </Link>
          <Badge className={statusInfo.color} data-testid="sav-status-badge">{statusInfo.text}</Badge>
        </div>
        <div className="px-3 pb-3">
          <h1 className="font-bold text-lg leading-tight">{sav.client_name}</h1>
          {sav.order_reference && (
            <p className="text-xs text-white/80 font-mono mt-0.5">Commande {sav.order_reference}</p>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* Coordonnées + adresse */}
        <Card>
          <CardContent className="pt-4 space-y-3">
            {sav.client_address && (
              <div className="flex items-start gap-2.5">
                <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm">{sav.client_address}</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(sav.client_address)}`}
                    target="_blank" rel="noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="open-maps-link"
                  >
                    Ouvrir dans Plans →
                  </a>
                </div>
              </div>
            )}
            {sav.client_phone && (
              <a href={`tel:${sav.client_phone}`} className="flex items-center gap-2.5 hover:bg-blue-50 -m-1 p-1 rounded" data-testid="call-client-link">
                <Phone className="h-5 w-5 text-primary" />
                <span className="text-sm text-blue-600 font-mono">{sav.client_phone}</span>
              </a>
            )}
            {sav.client_email && (
              <a href={`mailto:${sav.client_email}`} className="flex items-center gap-2.5 hover:bg-blue-50 -m-1 p-1 rounded">
                <Mail className="h-5 w-5 text-primary" />
                <span className="text-sm text-blue-600">{sav.client_email}</span>
              </a>
            )}

            {/* Interlocuteurs */}
            {(sav.client_interlocutors || []).length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Interlocuteurs</p>
                <div className="space-y-1.5">
                  {sav.client_interlocutors.map((it, i) => (
                    <div key={i} className="text-xs">
                      <span className="font-medium">{it.name}</span>
                      {it.role && <span className="text-muted-foreground"> · {it.role}</span>}
                      {it.phone && (
                        <a href={`tel:${it.phone}`} className="ml-1 text-blue-600">{it.phone}</a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Intervention */}
        <Card>
          <CardContent className="pt-4 space-y-2.5">
            <div className="grid grid-cols-2 gap-2.5">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Date</p>
                  <p className="text-sm font-medium">{sav.intervention_date || "Non planifié"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Durée</p>
                  <p className="text-sm font-medium">{sav.duration_days} jour(s)</p>
                </div>
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <Users className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Équipe</p>
                  <p className="text-sm font-medium">
                    {sav.operators} op. · {(sav.employee_names || []).join(", ") || "—"}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t">
              <p className="text-[10px] text-muted-foreground uppercase mb-1">Descriptif</p>
              <p className="text-sm whitespace-pre-wrap">{sav.description}</p>
            </div>
            {sav.notes && (
              <div className="pt-2 border-t">
                <p className="text-[10px] text-muted-foreground uppercase mb-1">Notes internes</p>
                <p className="text-xs text-muted-foreground">{sav.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photos avant/après */}
        <Card>
          <CardContent className="pt-4 space-y-3">
            <p className="text-sm font-semibold flex items-center gap-2">
              <Camera className="h-4 w-4 text-primary" /> Photos
            </p>
            <div className="grid grid-cols-2 gap-2">
              <input ref={beforeInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => handlePhotoUpload(e.target.files?.[0], "before")} />
              <input ref={afterInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => handlePhotoUpload(e.target.files?.[0], "after")} />
              <Button
                variant="outline"
                className="h-20 flex-col gap-1"
                onClick={() => beforeInputRef.current?.click()}
                disabled={uploadingLabel !== null}
                data-testid="photo-before-btn"
              >
                {uploadingLabel === "before" ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-5 w-5" />}
                <span className="text-xs">Photo « avant »</span>
              </Button>
              <Button
                variant="outline"
                className="h-20 flex-col gap-1"
                onClick={() => afterInputRef.current?.click()}
                disabled={uploadingLabel !== null}
                data-testid="photo-after-btn"
              >
                {uploadingLabel === "after" ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-5 w-5" />}
                <span className="text-xs">Photo « après »</span>
              </Button>
            </div>
            {(sav.attachments || []).filter((a) => a.is_image).length > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {(sav.attachments || []).filter((a) => a.is_image).length} photo(s) déjà jointe(s)
              </p>
            )}
          </CardContent>
        </Card>

        {/* Note ouvrier */}
        <Card>
          <CardContent className="pt-4 space-y-2">
            <p className="text-sm font-semibold">Ajouter une note</p>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex: Pièce manquante, retour prévu jeudi..."
              rows={3}
              className="text-sm"
              data-testid="worker-note-input"
            />
            <Button
              onClick={handleSendNote}
              disabled={!note.trim() || sendingNote}
              className="w-full"
              data-testid="send-worker-note"
            >
              {sendingNote ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Envoyer
            </Button>

            {/* Historique notes */}
            {(sav.worker_notes || []).length > 0 && (
              <div className="pt-3 border-t mt-3 space-y-2 max-h-48 overflow-y-auto">
                {(sav.worker_notes || []).slice().reverse().map((n, i) => (
                  <div key={n.id || i} className="bg-slate-50 p-2 rounded text-xs">
                    <p className="text-slate-700">{n.text}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {n.author_name} · {new Date(n.created_at).toLocaleString("fr-FR")}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-3 flex gap-2 z-20">
        {sav.status === "planifie" && (
          <Button
            className="flex-1 h-12"
            onClick={() => handleStatusChange("en_cours")}
            disabled={updating}
            data-testid="start-intervention-btn"
          >
            {updating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Démarrer
          </Button>
        )}
        {sav.status === "en_cours" && (
          <Button
            className="flex-1 h-12 bg-green-600 hover:bg-green-700"
            onClick={() => handleStatusChange("termine")}
            disabled={updating}
            data-testid="finish-intervention-btn"
          >
            {updating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Terminer l'intervention
          </Button>
        )}
        {sav.status === "termine" && (
          <Button variant="outline" className="flex-1 h-12" disabled>
            <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
            Intervention terminée
          </Button>
        )}
        {sav.status === "annule" && (
          <Button variant="outline" className="flex-1 h-12" disabled>Annulée</Button>
        )}
      </div>
    </div>
  );
}
